import priceFormat from "../../utils/priceFormat";
import { formatPhoneNumber } from "../../utils/phoneFormat";

/**
 * Formatters shared by the Upcoming Listings card, its detail view and the
 * presentation slides.
 *
 * Every one of these returns `null` when the listing has nothing to show, so a
 * caller can render the row or skip it. Empty fields are hidden rather than
 * printed as "0" or "—": a broker on a shared screen should see only what is
 * actually known about the property.
 */

export const formatPrice = (price) =>
  price || price === 0 ? `$${priceFormat(price) ?? price}` : null;

export const formatAddress = (listing) => listing?.streetAddr || null;

export const formatCityLine = (listing) => {
  if (!listing) return null;
  const cityState = [listing.city, listing.state].filter(Boolean).join(", ");
  return [cityState, listing.zip].filter(Boolean).join(" ") || null;
};

/** "4 bd · 3 ba · 2,410 sqft" — only the parts that have a value. */
export const formatSpecs = (listing) => {
  if (!listing) return null;
  const parts = [];
  if (listing.beds) parts.push(`${listing.beds} bd`);
  if (listing.baths) parts.push(`${listing.baths} ba`);
  if (listing.sqFt) parts.push(`${listing.sqFt.toLocaleString()} sqft`);
  return parts.length ? parts.join(" · ") : null;
};

const PROPERTY_TYPE_LABELS = { sale: "For Sale", rent: "For Rent" };
const PROPERTY_CATEGORY_LABELS = {
  under_construction: "New / Under Construction",
  land: "Land / Lot",
  sale: "For Sale",
  rent: "For Rent",
};

export const formatPropertyType = (type) =>
  type ? PROPERTY_TYPE_LABELS[type] || type : null;

export const formatPropertyCategory = (category) =>
  category ? PROPERTY_CATEGORY_LABELS[category] || category : null;

/** "0.34 ac · Built 2016 · For Sale" */
export const formatMeta = (listing) => {
  if (!listing) return null;
  const parts = [];
  if (listing.acreage) parts.push(`${listing.acreage} ac`);
  if (listing.yearBuilt) parts.push(`Built ${listing.yearBuilt}`);
  const category = formatPropertyCategory(listing.propertyCategory);
  const type = formatPropertyType(listing.propertyType);
  if (category) parts.push(category);
  else if (type) parts.push(type);
  return parts.length ? parts.join(" · ") : null;
};

export const formatPricePerSqFt = (listing) => {
  if (!listing?.price || !listing?.sqFt) return null;
  return `$${Math.round(listing.price / listing.sqFt).toLocaleString()} / sqft`;
};

const TIME_OPTS = { hour: "numeric", minute: "2-digit", hour12: true };
const DATE_OPTS = { weekday: "short", month: "short", day: "numeric" };

/** "Sat, Sep 7 · 1:00 – 4:00 PM" for one open house, or null if unparseable. */
export const formatOpenHouse = (openHouse) => {
  if (!openHouse?.startTime) return null;
  const start = new Date(openHouse.startTime);
  if (Number.isNaN(start.getTime())) return null;

  const date = start.toLocaleDateString("en-US", DATE_OPTS);
  const from = start.toLocaleTimeString("en-US", TIME_OPTS);

  const end = openHouse.endTime ? new Date(openHouse.endTime) : null;
  const to = end && !Number.isNaN(end.getTime())
    ? end.toLocaleTimeString("en-US", TIME_OPTS)
    : null;

  return `${date} · ${from}${to ? ` – ${to}` : ""}`;
};

export const formatAgentName = (user) =>
  user ? [user.firstName, user.lastName].filter(Boolean).join(" ") || null : null;

/** "Sep 6, 2026" — the way a go-live date is read out in a meeting. */
export const formatLongDate = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const d = new Date(typeof value === "string" ? parseInt(value, 10) : value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

/**
 * Phone and e-mail on one line, whichever of them exists. Uses "--" for neither
 * so the caller can drop the line entirely.
 */
export const formatAgentContact = (user) => {
  const phone = formatPhoneNumber(user?.phone);
  return [phone && phone !== "--" ? phone : null, user?.email]
    .filter(Boolean)
    .join(" · ") || null;
};

/** "Lic. #0654321 · Main Office" — credentials line under the agent's name. */
export const formatAgentCredentials = (user, office) =>
  [user?.licenseNo && `Lic. #${user.licenseNo}`, office?.name]
    .filter(Boolean)
    .join(" · ") || null;
